<script setup>
import { inject, ref, computed, onMounted, onServerPrefetch } from 'vue'
import { getArticleRecommend } from '@/api/article.js'
import { action } from '@/utils/gtm'
import SmallCard from '../../../components/SmallCard/index.vue'
import ArticleGhostCard from '../../../components/GhostCard/index.vue'
import { checkVideo } from '@/utils'

const size = inject('size')
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
const articleList = inject('articleList')
const categoryId = computed(() => articleList.value?.[0]?.category_id)
const excludeId = computed(() => articleList.value?.map((v) => v.id))

const loading = ref(false)
const list = ref([])
const isEmpty = computed(() => list.value.length === 0)
const mlist = computed(() => list.value.filter((v, i) => i > 0))
const fetchArticleRecommend = async () => {
  if (!categoryId.value) return
  loading.value = true
  try {
    const res = await getArticleRecommend({
      category_id: categoryId.value,
      exclude_ids: excludeId.value
    })
    if (res.code !== 0) throw new Error(res.message)
    list.value = (res.data?.list || []).slice(0, 5)
    loading.value = false
  } catch (error) {
    loading.value = false
  }
}

const layerData = inject('layerData')
const tracking = (item) => {
  action({
    ...layerData.value,
    page_title: item.title || '',
    screen_class: item.title || '',
    eventCategory: 'sub_category',
    eventLabel: item.title || '',
    eventID: 'B015',
    screenLabel: item.link || ''
  })
}

// 调用时机为没有下一篇且可见时
onMounted(() => fetchArticleRecommend())

onServerPrefetch(async () => {
  // await fetchArticleRecommend()
})
</script>

<template>
  <div
    v-if="loading ? true : !isEmpty"
    class="tvb-article-recommend"
    :class="size ? 'recommend-' + size : ''"
  >
    <div class="tvb-article-recommend_tit">推薦內容</div>
    <div v-if="!isMobile" class="tvb-article-recommend_cont">
      <template v-if="loading">
        <div
          v-for="(item, i) in 5"
          :key="item"
          class="tvb-skeleton tvb-article-recommend_card tvb-hairline--surround"
          :class="{ 'tvb-article-recommend_card-frist': i === 0 }"
        >
          <tvb-image src="" backgroundc="#F5F6F7"></tvb-image>
        </div>
      </template>
      <template v-else>
        <SmallCard
          v-for="(item, i) in list"
          :key="item.id"
          class="tvb-article-recommend_card"
          :class="{ 'tvb-article-recommend_card-frist': i === 0 }"
          :img="item.image_info"
          :title="item.title"
          title-tag="p"
          :iconSize="i === 0 ? 40 : 32"
          :isVideo="checkVideo(item)"
          :link="{ url: item.link, rel: item.link_rel, target: item.link_target }"
          @tracking="tracking(item)"
        ></SmallCard>
      </template>
    </div>
    <div v-else class="tvb-article-recommend_cont-m">
      <tvb-article-card
        class="tvb-article-recommend_card-m-frist"
        :loading="loading"
        :size="size"
        :autoheight="isMobile"
        :img="list[0].image_info"
        :isVideo="checkVideo(list[0])"
        :playIcon="{ size: 40 }"
        :title="list[0].title"
        :link="{ url: list[0].link, rel: list[0].link_rel, target: list[0].link_target }"
        @tracking="tracking(item)"
      ></tvb-article-card>

      <n-scrollbar x-scrollable>
        <div class="tvb-article-recommend_scroll-wrap">
          <template v-if="loading">
            <article-ghost-card v-for="item in 3" :key="item" loading></article-ghost-card>
          </template>
          <template v-else>
            <article-ghost-card
              v-for="item in mlist"
              :key="item.id"
              :size="size"
              :img="item.image_info"
              :isVideo="checkVideo(item)"
              :playIcon="{ size: 32 }"
              :title="item.title"
              :link="{ url: item.link, rel: item.link_rel, target: item.link_target }"
              @tracking="tracking(item)"
            ></article-ghost-card>
          </template>
        </div>
      </n-scrollbar>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-article-recommend {
  margin-top: 56px;
  .tvb-article-recommend_tit {
    font-size: 22px;
    font-weight: 500;
    color: #1e1e1e;
    line-height: 32px;
    letter-spacing: 0.5px;
    margin-bottom: 16px;
  }

  &.recommend-s,
  &.recommend-xs {
    margin-top: 22px;
    .tvb-article-recommend_tit {
      font-size: 17px;
      line-height: 24px;
      margin-bottom: 10px;
      padding: 0 var(--tvb-article-detail-m-padding);
    }
  }

  .tvb-article-recommend_cont {
    --gap: 16px;
    --columns: 4;
    gap: var(--gap);
    display: grid;
    grid-template-columns: repeat(var(--columns), 1fr);
    .tvb-skeleton {
      border-radius: 8px;
      --tvb-hairline-border-radius: calc(2 * 8px);
      overflow: hidden;
      @include safari-overflow-hidden();
    }
    .tvb-article-recommend_card {
      aspect-ratio: calc(308px / 173px);
      &.tvb-article-recommend_card-frist {
        grid-column: 1/3;
        grid-row: 1/3;
        aspect-ratio: calc(632px / 362px);
        --sc-mask-padding: 16px 16px;
      }
      &:not(.tvb-article-recommend_card-frist) {
        --sc-title-font-size: 16px;
        --sc-title-line-height: 22px;
        --sc-icon-margin-left: 12px;
        --sc-mask-padding: 10px 12px;
      }
    }
  }

  .tvb-article-recommend_cont-m {
    .tvb-article-recommend_card-m-frist {
      margin-bottom: 10px;
      margin-left: var(--tvb-article-detail-m-padding);
      margin-right: var(--tvb-article-detail-m-padding);
    }
    :deep(.n-scrollbar-rail__scrollbar) {
      opacity: 0;
    }
    .tvb-article-recommend_scroll-wrap {
      display: flex;
      gap: 10px;
      padding: 0 var(--tvb-article-detail-m-padding);
      :deep(.tvb-article-ghost-card) {
        width: 160px;
        --gc-info-margin-top: 6px;
      }
    }
  }
}
</style>
